/**
 * Design tokens for the block tools: the numbers a model never writes.
 *
 * A unit is picked by preset, text by role, space and radius by step. Each resolves to metres (or a font size) from the unit's width
 * `W` when the build is collected, so the scene holds plain numbers and no format changes. Colours and fonts are not here: they bind
 * to the Style role tokens (`{cardColor}`, `{titleFont}`), and {@link THEME_DEFAULTS} only supplies the values those resolve to until
 * a Style is applied. Pure data and arithmetic; the build entry has no dependencies beyond this folder, `src/layout/legibility` and the theme vocabulary.
 */
import { MIN_TEXT_DU, REFERENCE_CANVAS_DU, WCAG_AA_TEXT_CONTRAST, contrastRatio } from '../../layout/legibility';
import { TEXT_SCALE_LADDER } from '../../scenedata/theme-vocabulary';

/** The unit's width in metres, and the width-to-height ratio a `stage` unit is given. */
export const UNIT_PRESETS = {
    poster: { width: 0.6, stageAspect: 3 / 4 },
    landscape: { width: 1.2, stageAspect: 16 / 9 },
    card: { width: 0.4, stageAspect: 1 },
    tabletop: { width: 0.6, stageAspect: 1 },
} as const;
export type UnitPreset = keyof typeof UNIT_PRESETS;
export const UNIT_KINDS = ['page', 'stage', 'composite'] as const;
export type UnitKind = typeof UNIT_KINDS[number];

/** The type floor as a share of the unit width: the composer's 20 du on its 1280 du reference canvas, so both paths agree. */
export const TYPE_FLOOR_FRACTION = MIN_TEXT_DU / REFERENCE_CANVAS_DU;

/**
 * The type ramp: the prototype's twelve rungs, label to display-2 (AILayout_Prototype public/semantic/tokens.json `typography`), in du
 * on its 1280 du reference canvas, with each rung's line height and weight. As a share of the unit width the ramp is lifted as a whole by
 * {@link RAMP_LIFT}, so its smallest rung sits on the floor share and its steps keep their proportions; in a unit narrower than about
 * 0.86 m the physical floor ({@link typeEmM}) holds the small rungs up and compresses the steps.
 */
export const TYPE_RAMP = [
    { rung: 'label', du: 11, lineHeight: 1.3, weight: 500 },
    { rung: 'caption', du: 12, lineHeight: 1.4, weight: 400 },
    { rung: 'body2', du: 14, lineHeight: 1.5, weight: 400 },
    { rung: 'body1', du: 16, lineHeight: 1.5, weight: 400 },
    { rung: 'h6', du: 18, lineHeight: 1.4, weight: 500 },
    { rung: 'h5', du: 20, lineHeight: 1.3, weight: 600 },
    { rung: 'h4', du: 24, lineHeight: 1.25, weight: 600 },
    { rung: 'h3', du: 30, lineHeight: 1.25, weight: 600 },
    { rung: 'h2', du: 36, lineHeight: 1.2, weight: 600 },
    { rung: 'h1', du: 48, lineHeight: 1.15, weight: 700 },
    { rung: 'display', du: 60, lineHeight: 1.1, weight: 700 },
    { rung: 'display-2', du: 72, lineHeight: 1.08, weight: 700 },
] as const;
export type Rung = typeof TYPE_RAMP[number]['rung'];
export const RUNGS: readonly Rung[] = TYPE_RAMP.map(r => r.rung);

/**
 * The prototype's web ramp starts at 11 du; its AR floor is 20 du ("nothing in this scale is fine print", docs/M3_TYPE_SCALE_PLAN.md).
 * Lifting the ramp by their ratio puts label on the floor. The same lift turns the prototype's spacing du into ours (see {@link protoSpace}),
 * so type and space keep the prototype's proportion to each other.
 */
export const RAMP_LIFT = MIN_TEXT_DU / TYPE_RAMP[0].du;

/**
 * The smallest em-square, in metres, of the ramp's smallest rung, however narrow the unit. Legibility is angular (capital height over
 * viewing distance). The width ramp holds the angle while a unit is read from a distance that grows with its width, but a narrow unit is
 * still read from a metre or more, so its type stops shrinking here: a capital of about 9.5 mm, 33′ at 1 m and 16′ (the least that can
 * be read at all) at 2 m. The ramp's share reaches it at a unit width of {@link TYPE_FLOOR_MIN_M} / {@link TYPE_FLOOR_FRACTION}, 0.864 m.
 */
export const TYPE_FLOOR_MIN_M = 0.0135;

/**
 * How fast the physical floor grows with the prototype size, as a power: 1 would keep the ramp's steps, 0 would set every rung at the
 * floor. Headings grow far less than the ramp, so they fit a narrow line. Two sizes that both sit on the physical floor keep their ramp
 * ratio to the power 0.55: `title` (h3) is 1.52 times `body` (body2) and 1.41 times body1, and 32 px is 1.46 times 16 px, above the
 * quality check's `MIN_TITLE_TO_BODY` (4/3). Pairs closer than 1.69 to 1 on the ramp fall under 4/3 there: h4 over body1 is 1.25 (1.5
 * on the ramp).
 */
export const TYPE_FLOOR_GROWTH = 0.55;

/**
 * The em-square, metres, of type the prototype sets at `du` (on its 1280 du canvas, times `lift`) in a unit `w` wide: the width ramp, held
 * to the floor share and to the physical floor, which grows with `du` by {@link TYPE_FLOOR_GROWTH}. A `du` under the smallest rung's
 * (0 for the floor itself) takes the smallest rung's physical floor. `lift` is {@link RAMP_LIFT} for the ramp and 1 for a page's own px.
 * The ramp's rungs leave the physical floor by a unit width of about 0.86 m; px text, with no lift, leaves it wider, latest at 20 px
 * (1.20 m; 16 px at 1.06 m, 24 px at 1.11 m).
 */
export function typeEmM(du: number, w: number, lift: number = RAMP_LIFT): number {
    const least = TYPE_RAMP[0].du;
    const physical = TYPE_FLOOR_MIN_M * (Math.max(du, least) / least) ** TYPE_FLOOR_GROWTH;
    return Math.max(du * lift / REFERENCE_CANVAS_DU * w, TYPE_FLOOR_FRACTION * w, physical);
}

/**
 * The five roles a model was first given, as rungs (they were shares of the unit width). `caption` is the ramp's rung of that name and `body`
 * (0.020 before) and `heading` land near their earlier sizes; `title` (0.040 before) grows about 6% and `display` (0.060 before) to about 0.085.
 */
export const TYPE_ROLES = { display: 'display', title: 'h3', heading: 'h5', body: 'body2', caption: 'caption' } as const satisfies Record<string, Rung>;
export type TypeRole = keyof typeof TYPE_ROLES;

/** A rung by its name, or a role's rung. Null when it is neither. */
export function rungOf(name: unknown): Rung | null {
    if (typeof name !== 'string') return null;
    if (Object.hasOwn(TYPE_ROLES, name)) return TYPE_ROLES[name as TypeRole];
    return (RUNGS as readonly string[]).includes(name) ? name as Rung : null;
}

/** `by` rungs down the ramp (up when negative), held to the ends. */
export function stepRung(rung: Rung, by: number): Rung {
    return RUNGS[Math.max(0, Math.min(RUNGS.length - 1, RUNGS.indexOf(rung) - by))];
}

/** The Style's text role a rung is set in: h3 and up in the title font, h6 to h4 in the header font, the rest in the body font. */
export function fontRoleOf(rung: Rung): 'title' | 'header' | 'body' {
    const i = RUNGS.indexOf(rung);
    return i >= RUNGS.indexOf('h3') ? 'title' : i >= RUNGS.indexOf('h6') ? 'header' : 'body';
}

/** A picked Style's values, as the Style vocabulary names them (`titleFont`, `bodyScale`, `cardColor`, ...). */
export type StyleValues = Readonly<Record<string, unknown>>;

export interface RungType { fontSize: number; lineHeight: number; bold: boolean; font: 'title' | 'header' | 'body' }

/**
 * How a rung is set in a unit `w` wide under a Style. The Style's `{role}Scale` is read as its deviation from the standard ladder
 * ({@link TEXT_SCALE_LADDER}; absent means the ladder) and scales that role's rungs; its `{role}LineHeight` and `{role}Weight` replace the
 * rung's own. Without a Style the prototype's ramp stands wherever it is above the physical floor ({@link typeEmM}); never under the floor.
 */
export function rungType(rung: Rung, w: number, style?: StyleValues): RungType {
    const step = TYPE_RAMP[RUNGS.indexOf(rung)];
    const font = fontRoleOf(rung);
    const num = (key: string): number | undefined => {
        const v = style?.[key];
        return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : undefined;
    };
    const scale = (num(`${font}Scale`) ?? TEXT_SCALE_LADDER[font].scale) / TEXT_SCALE_LADDER[font].scale;
    return {
        fontSize: round(typeEmM(step.du * scale, w) * 100, 4),
        lineHeight: num(`${font}LineHeight`) ?? step.lineHeight,
        bold: (num(`${font}Weight`) ?? step.weight) >= 600,
        font,
    };
}

/** Space steps as a share of the unit width, lifted with the type floor in a narrow unit ({@link spaceLift}). `s0` is none. */
export const SPACING = { s0: 0, s1: 0.01, s2: 0.02, s3: 0.03, s4: 0.05, s5: 0.08 } as const;
export type SpaceToken = keyof typeof SPACING;

/**
 * The prototype's Dominant + Sidebar template (public/ai-templates/system/dominant-sidebar.template.json): on its 1280 du canvas the
 * sidebar is 360 du, the gap 120 du, and the main panel fills the rest, 800 du.
 */
export const DOMINANT_SIDEBAR = { canvas: 1280, main: 800, sidebar: 360, gap: 120 } as const;

/** How much more a lead zone grows than a sibling beside it: the template's main panel to its sidebar. */
export const LEAD_WEIGHT = DOMINANT_SIDEBAR.main / DOMINANT_SIDEBAR.sidebar;

/**
 * Density: the prototype's rule for a column that fills up (systemPromptV5.prompt.ts, Sizing & Spacing). 5+ items: text-sm, gap-2, p-4;
 * 7+ items: text-xs, gap-1, p-4. Text steps down a rung for each (base to sm, sm to xs); gap and padding are its du, never raised.
 */
export const DENSITY = [
    { items: 7, rungsDown: 2, gapDu: 4, paddingDu: 16 },
    { items: 5, rungsDown: 1, gapDu: 8, paddingDu: 16 },
] as const;

/** Corner radius steps as a share of the unit width. */
export const RADIUS = { r1: 0.01, r2: 0.02, r3: 0.04 } as const;
export type RadiusToken = keyof typeof RADIUS;

/**
 * The narrowest an item in a wrapping row may get before it moves to the next line, as a share of the unit width. Just under a quarter,
 * a third and a half, so that whole rows of items do not land exactly on the edge of the line, where the engine's float compare decides.
 */
export const ITEM_MIN = { s: 0.23, m: 0.32, l: 0.47 } as const;
export type ItemMinToken = keyof typeof ITEM_MIN;

/** Image shapes by name; any `w:h` or positive ratio is accepted as well. */
export const ASPECT_NAMES = { square: 1, landscape: 4 / 3, wide: 16 / 9, portrait: 3 / 4, tall: 9 / 16 } as const;

/** The families the engine bundles (`bundledFontFamilies()`; a test pins the two together). */
export const BUNDLED_FONTS = ['Roboto', 'Inter', 'Source Serif 4', 'JetBrains Mono', 'Oswald', 'Bungee', 'Caveat'] as const;

/** Surfaces a panel, button or chip can sit on. Each has a colour and the colour of content on it in the Style vocabulary. */
export const SURFACES = ['background', 'card', 'panel', 'muted', 'primary', 'accent'] as const;
export type Surface = typeof SURFACES[number];

/** Values the Style tokens take until a Style is applied. Every surface pair passes WCAG 4.5:1 (pinned by a test). */
export const THEME_DEFAULTS: Readonly<Record<string, string>> = {
    backgroundColor: '#EFEDE8', backgroundTextColor: '#2A2724',
    cardColor: '#F8F7F4', cardTextColor: '#2A2724',
    panelColor: '#FFFFFF', panelTextColor: '#2A2724',
    mutedColor: '#DDD9D1', mutedTextColor: '#2A2724',
    primaryColor: '#2F5D8A', primaryTextColor: '#FFFFFF',
    accentColor: '#A8502C', accentTextColor: '#FFFFFF',
    titleFont: 'Roboto', headerFont: 'Roboto', bodyFont: 'Roboto',
};

const round = (n: number, places = 6) => Math.round(n * 10 ** places) / 10 ** places;

export function unitWidth(preset: UnitPreset): number { return UNIT_PRESETS[preset].width; }

/**
 * How much the physical floor ({@link typeEmM}) lifts body text in a unit `w` wide: 1 from about 0.78 m, where body text leaves the floor,
 * 2.6 at 0.3 m. Space steps are shares of the width like the ramp, so the build lifts them by this factor to keep their proportion to the
 * text (space-lift.ts).
 */
export function spaceLift(w: number): number {
    const body = TYPE_RAMP[RUNGS.indexOf(TYPE_ROLES.body)].du;
    return typeEmM(body, w) / (body * RAMP_LIFT / REFERENCE_CANVAS_DU * w);
}

/** Metres for a space step at unit width `w`. */
export function space(token: SpaceToken, w: number): number { return round(SPACING[token] * w); }
export function radius(token: RadiusToken, w: number): number { return round(RADIUS[token] * w); }
export function itemMin(token: ItemMinToken, w: number): number { return round(ITEM_MIN[token] * w); }

/**
 * The em-square, metres, the build lifts any text in a unit `w` wide to: the floor share of the width or {@link TYPE_FLOOR_MIN_M}, whichever
 * is larger. The quality checks' `typeFloorM` is the share alone.
 */
export function buildTypeFloorM(w: number): number { return typeEmM(0, w); }

/** `Text.fontSize` for a role or rung with no Style: the em-square in centi-world-units (100 is one metre). */
export function fontSizeFor(role: TypeRole | Rung, w: number): number { return rungType(rungOf(role)!, w).fontSize; }

/** Metres for a spacing value the prototype writes in du (its `gap-2` is 8), lifted with the type so the two keep their proportion. */
export function protoSpace(du: number, w: number): number { return round(du * RAMP_LIFT / REFERENCE_CANVAS_DU * w); }

/** Lift a `Text.fontSize` under the floor to the floor. */
export function liftToFloor(fontSize: number, w: number): number { return Math.max(fontSize, round(buildTypeFloorM(w) * 100, 4)); }

/** Read an aspect: a ratio, `"w:h"`, or a name from {@link ASPECT_NAMES}. Null when it is none of those. */
export function parseAspect(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) && value > 0.1 && value < 10 ? value : null;
    if (typeof value !== 'string') return null;
    if (Object.hasOwn(ASPECT_NAMES, value)) return ASPECT_NAMES[value as keyof typeof ASPECT_NAMES];
    const m = /^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/.exec(value.trim());
    return m && +m[2] > 0 ? parseAspect(+m[1] / +m[2]) : null;
}

/** The Style token a surface's fill binds to, and the one its content binds to. */
export const surfaceFill = (surface: Surface): string => `{${surface}Color}`;
export const surfaceText = (surface: Surface): string => `{${surface}TextColor}`;

/** True when the default pair for `surface` reads at the text minimum; a Style that breaks it is the finish pass's to repair. */
export function themePairReads(surface: Surface): boolean {
    return contrastRatio(THEME_DEFAULTS[`${surface}TextColor`], THEME_DEFAULTS[`${surface}Color`]) >= WCAG_AA_TEXT_CONTRAST;
}

/**
 * Declarations that give the unit root its Style tokens, so bindings resolve before any Style is applied. With a picked Style, its values
 * stand in for the defaults.
 */
export function themeDeclarations(style?: StyleValues): { type: 'color' | 'string'; name: string; value: string }[] {
    return Object.entries(THEME_DEFAULTS).map(([name, fallback]) => {
        const given = style?.[name];
        const value = typeof given === 'string' && given ? given : fallback;
        return { type: name.endsWith('Font') ? 'string' : 'color', name, value };
    });
}
